import React from "react";
import { StagingTitelineAppActivity } from "../types/StagingTitelineAppActivity";
import { ActivityAndBreakdownData } from "../types/ActivityAndBreakdownData";
import {fetchBreakdownsByPid} from "./BreakdownsApiFetch";
import dayjs from "dayjs";
import {message} from "antd";
import { getApiUrl } from '../config/api';

export const fetchActivitiesByPid = async (pid?: number, aid: number = -1): Promise<ActivityAndBreakdownData[]> => {
    const validPid = pid ?? -1;
    const apiUrl = getApiUrl(`/api/TitelineActivity/GetActivityByPid/${validPid}`);

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            if (response.status === 404) {
                console.warn(`No activities found for Pid: ${validPid} (404 Not Found)`);
            } else {
                console.error(`Error fetching activities for Pid: ${validPid} - ${response.status} ${response.statusText}`);
            }
            return [];
        }

        let backendData: StagingTitelineAppActivity[] = await response.json();
        if (pid === -1) {
            backendData = backendData.filter((activity) => activity.DataSource === aid.toString());
        }

        return backendData.map((activity) => ({
            key: activity.Actid,
            aob_id: activity.Actid,
            pid: activity.Pid,
            hole_id: activity.HoleID,
            aob_type: `ACTIVITY:${activity.ActivityName}`,
            time_start: activity.TimeStart,
            time_finish: activity.TimeFinish,
            total_hours: activity.Hours,
        }));
    } catch (error) {
        console.error("Error in fetchActivitiesByPid API call:", error);
        return [];
    }
};

export const addActivityRow = async (activityRow: ActivityAndBreakdownData, aid: number = -1) => {
    const apiUrl = getApiUrl('/api/TitelineActivity/AddActivity');
    const cloudPayload = [{
        Pid: activityRow.pid ?? -1,
        HoleID: activityRow.hole_id,
        ActivityName: activityRow.aob_type,
        TimeStart: activityRow.time_start,
        TimeFinish: activityRow.time_finish,
        Hours: activityRow.total_hours,
        DataSource: aid,
    }];

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(cloudPayload),
        });

        if (response.ok) {
            console.log("Activity row added to cloud successfully.");
            return true;
        } else {
            console.error("Failed to add activity row to cloud:", response.status, response.statusText);
            return false;
        }
    } catch (error) {
        console.error("Error in addActivityRow API call:", error);
        return false;
    }
};

export const updateActivityRow = async (actid: number, updatedRow: ActivityAndBreakdownData) => {
    const apiUrl = getApiUrl(`/api/TitelineActivity/UpdateActivityByActid/${actid}`);
    const cloudPayload = {
        HoleID: updatedRow.hole_id,
        ActivityName: updatedRow.aob_type,
        TimeStart: updatedRow.time_start,
        TimeFinish: updatedRow.time_finish,
        Hours: updatedRow.total_hours,
    };

    try {
        const response = await fetch(apiUrl, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(cloudPayload),
        });

        if (response.ok) {
            console.log(`Activity row with Actid ${actid} updated successfully.`);
            return true;
        } else {
            const errorMessage = await response.text();
            console.error(`Failed to update activity row with Actid ${actid}:`, response.status, response.statusText, errorMessage);
            return false;
        }
    } catch (error) {
        console.error("Error in updateActivityRow API call:", error);
        return false;
    }
};

export const deleteActivityByActid = async (actid: number) => {
    const apiUrl = getApiUrl(`/api/TitelineActivity/DeleteActivityByActid/${actid}`);

    try {
        const response = await fetch(apiUrl, {
            method: "DELETE",
        });

        if (response.ok) {
            const responseData = await response.json();
            console.log("DeleteActivityByActid API response:", responseData);
            return true;
        } else {
            console.error("Failed to delete activity row from the cloud:", response.status, response.statusText);
            return false;
        }
    } catch (error) {
        console.error("Error in deleteActivityByActid API call:", error);
        return false;
    }
};

export const getAllActivitiesAndBreakdownsByPid = async (pid: number, aid: number): Promise<ActivityAndBreakdownData[]> => {
    const activities = await fetchActivitiesByPid(pid, aid);
    const breakdowns = await fetchBreakdownsByPid(pid, aid);

    const all = [...activities, ...breakdowns];

    return all.sort((a, b) => {
        const tA = dayjs(a.time_start, "h:mm A");
        const tB = dayjs(b.time_start, "h:mm A");

        if (!tA.isValid()) return 1;
        if (!tB.isValid()) return -1;

        return tA.isBefore(tB) ? -1 : tA.isAfter(tB) ? 1 : 0;
    });
};

export const sortActivitiesByShift = (
    activitiesAndBreakdowns: ActivityAndBreakdownData[],
    isDayShift: boolean = true
): ActivityAndBreakdownData[] => {
    if (isDayShift) return activitiesAndBreakdowns;

    let pivotIndex = 0;

    for (let i = 0; i < activitiesAndBreakdowns.length; i++) {
        const time = activitiesAndBreakdowns[i].time_start;
        if (time?.includes("PM") && time >= "6:00 PM") {
            pivotIndex = i;
            break;
        }
    }

    return [
        ...activitiesAndBreakdowns.slice(pivotIndex),
        ...activitiesAndBreakdowns.slice(0, pivotIndex),
    ];
};

export const fetchTotalActivityHours = async (pid?: number, ableMessage: boolean = true, aid: number = 0): Promise<number> => {
    const validPid = pid ?? -1;
    const apiUrl = getApiUrl(`/api/TitelineActivity/CalculateTotalActivityHrs/${validPid}/${aid}`);

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Failed to fetch total hours. Status: ${response.status} - ${response.statusText}. Error: ${errorText}`);
            return 0;
        }

        const data = await response.json();

        const totalHours = data?.total_activity_hours ?? 0;

        if (ableMessage && totalHours > 12.2) {
            message.warning(`Total Activity Hours (${totalHours}) are unusually high (over 12 hrs). Please review.`);
        }

        return totalHours;
    } catch (error) {
        console.error("fetchTotalActivityHours error:", error);
        return 0;
    }
};

export const fetchTotalDrillingHours = async (pid?: number, aid: number = 0): Promise<number> => {
    const validPid = pid ?? -1;
    const apiUrl = getApiUrl(`/api/TitelineActivity/CalculateTotalDrillingHrs/${validPid}/${aid}`);

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Failed to fetch total drilling hours. Status: ${response.status} - ${response.statusText}. Error: ${errorText}`);
            return 0;
        }

        const data = await response.json();

        const totalHours = data?.total_drilling_hours ?? 0;

        return totalHours;
    } catch (error) {
        console.error("fetchTotalDrillingHours error:", error);
        return 0;
    }
};